import { useEffect, useState } from "react";
import { Megaphone, Eye, Send, FlaskConical, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

const LAUNCH = {
  subject: "New: Grades 1, 2 & 3 are here! / Nouveau : les Grades 1, 2 et 3 sont arrivés !",
  message: `Great news! Anou Appran now covers every primary grade, from Grade 1 to Grade 6.

For our youngest learners (Grades 1, 2 and 3), every question and every answer is read aloud, the text is big and easy to read, and many answers come with pictures. Subjects: Maths, English, French and Health & PE, plus SSEE in Grade 3. Each question has a short explanation written by our teachers.

Same prices as the other grades: Rs 200 for 1 subject, Rs 500 for 3 subjects, Rs 800 for all subjects, per child and per month. You can try a free demo first.

Log in at anouappran.com and add your younger child today!

Bonne nouvelle ! Anou Appran couvre désormais tout le primaire, du Grade 1 au Grade 6.

Pour les plus petits (Grades 1, 2 et 3), chaque question et chaque réponse est lue à voix haute, le texte est en gros caractères et beaucoup de réponses sont illustrées. Matières : Maths, Anglais, Français et Santé & EPS, ainsi que SSEE en Grade 3. Chaque question a une courte explication écrite par nos enseignants.

Mêmes prix que les autres Grades : Rs 200 pour 1 matière, Rs 500 pour 3 matières, Rs 800 pour toutes les matières, par enfant et par mois. Vous pouvez d'abord essayer la démo gratuite.

Connectez-vous sur anouappran.com et ajoutez votre plus jeune enfant dès aujourd'hui !`,
};

export default function OffersEmail() {
  const [info, setInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/offers").then(({ data }) => setInfo(data)).catch(() => setInfo({ audience: 0, sending_ready: false, campaigns: [] }));
  useEffect(() => { load(); }, []);

  const call = async (kind) => {
    if (kind === "send" && !window.confirm(`Send "${subject}" to ${info.audience} parent(s)?`)) return;
    setBusy(kind);
    try {
      const { data } = await api.post(`/admin/offers/${kind}`, { subject, message });
      if (kind === "preview") setPreview(data.html);
      if (kind === "test") toast.success(`Test email sent to ${data.to}`);
      if (kind === "send") { toast.success(`Sending to ${data.total} parent(s)…`); load(); }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };
  if (!info) return <Loader2 className="animate-spin mx-auto" />;
  const ok = subject.trim().length >= 3 && message.trim().length >= 10;
  const btn = "rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50";

  return (
    <div data-testid="admin-offers" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-slate-800 rounded-2xl border-2 border-amber-400/60 px-4 py-2 flex items-center gap-2" data-testid="offers-audience">
          <Megaphone size={18} className="text-amber-300" /> <span className="font-bold">{info.audience}</span>
          <span className="text-slate-300 text-sm">parent(s) ticked "news and offers"</span>
        </div>
        {!info.sending_ready && (
          <p className="text-sm text-amber-200 flex items-center gap-1.5" data-testid="offers-not-ready">
            <AlertTriangle size={15} /> Sending turns on once your Resend API key is added (from marieyanna@kmpmoris.com).
          </p>
        )}
      </div>
      <button onClick={() => { setSubject(LAUNCH.subject); setMessage(LAUNCH.message); setPreview(""); }} data-testid="offers-template-launch"
        className="rounded-full border-2 border-pink-400 text-pink-200 px-4 py-1.5 text-sm font-semibold hover:bg-pink-500/20">Use the Grades 1–3 launch template</button>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} placeholder="Subject, e.g. New Grade 6 chapters are here!"
        className="w-full h-11 rounded-xl bg-slate-800 border-2 border-slate-600 px-3" data-testid="offers-subject" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} maxLength={8000}
        placeholder={"Write your message. Leave an empty line between paragraphs.\nAn \"Open Anou Appran\" button and an Unsubscribe link are added automatically."}
        className="w-full rounded-xl bg-slate-800 border-2 border-slate-600 p-3" data-testid="offers-message" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => call("preview")} disabled={!ok || !!busy} className={`${btn} border-2 border-slate-500`} data-testid="offers-preview-btn">
          {busy === "preview" ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview</button>
        <button onClick={() => call("test")} disabled={!ok || !!busy || !info.sending_ready} className={`${btn} border-2 border-blue-400 text-blue-200`} data-testid="offers-test-btn">
          {busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} Send me a test</button>
        <button onClick={() => call("send")} disabled={!ok || !!busy || !info.sending_ready || !info.audience} className={`${btn} bg-yellow-300 text-slate-900`} data-testid="offers-send-btn">
          {busy === "send" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send to {info.audience} parent(s)</button>
      </div>
      {preview && <iframe title="Email preview" srcDoc={preview} sandbox="" className="w-full h-[520px] rounded-2xl bg-white" data-testid="offers-preview" />}
      {!!info.campaigns.length && (
        <div className="text-sm" data-testid="offers-history">
          <p className="font-bold mb-1">Sent emails</p>
          {info.campaigns.map((c) => (
            <p key={c.id} className="text-slate-300">{new Date(c.created_at).toLocaleString("en-GB")} · {c.subject} · {c.sent}/{c.total} sent{c.failed ? `, ${c.failed} failed` : ""} · {c.status}</p>
          ))}
        </div>
      )}
    </div>
  );
}
